import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import moment from 'moment';
import { useStore } from '../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import GeneralService from '../../../../../services/general';
import EmployeePerformanceService, { EmployeePerformanceProps } from '../../../../../services/employees/performance';
import EmployeeTargetSummaryService, { ITargetSummaryProps } from '../../../../../services/employees/targetSummary';

// components
import { ActionButton, ProgressIndicator, Stack } from '@fluentui/react';
import LoadingComponent from '../../../../feedbacks/loading';
import Text from '../../../../typography/text';
import Label from '../../../../typography/label';
import Tag from '../../../../uiframeworks/tag';

interface AdministratorDetailsPerformanceRecapProps {
    userId: string;
}

const AdministratorDetailsPerformanceRecap: React.FC<AdministratorDetailsPerformanceRecapProps> = (props: AdministratorDetailsPerformanceRecapProps) => {
    const { banner } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [performance, setPerformance] = React.useState<{ currentPeriod: EmployeePerformanceProps, previousPeriod: EmployeePerformanceProps } | undefined>();
    const [targetSummary, setTargetSummary] = React.useState<ITargetSummaryProps | undefined>();
    const [selectedGrouping, setSelectedGrouping] = React.useState<string>('created date');
    const [selectedDate, setSelectedDate] = React.useState<{ text: string; date: string; period: 'month' | 'year' }>({
        text: 'this month',
        date: moment().startOf('month').toISOString(),
        period: 'month'
    });

    const performanceService = new EmployeePerformanceService(props.userId);

    useEffect(() => {
        init();
    }, [selectedDate, selectedGrouping]);

    const init = async () => {
        try {
            setLoaded(false);

            const [perfResult, targetResult] = await Promise.all([
                performanceService.recap(selectedDate.date, selectedGrouping, selectedDate.period),
                EmployeeTargetSummaryService.get(props.userId, selectedDate.date, selectedGrouping, selectedDate.period).catch(() => undefined)
            ]);

            setPerformance(perfResult);
            setTargetSummary(targetResult);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_sales_recap_error',
                text: 'Failed to get sales recap. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
            setLoaded(true);
        }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign={'center'}>
            <Text size={'small'}>Based on </Text>
            <Stack horizontal>
                <Label size={'xsmall'} className={loaded ? 'color-blue' : 'color-gray'}>{selectedGrouping}</Label>
                <ActionButton disabled={!loaded} menuProps={{
                    items: [
                        { key: 'created date', text: 'created date', onClick: () => setSelectedGrouping('created date') },
                        { key: 'loading date', text: 'loading date', onClick: () => setSelectedGrouping('loading date') }
                    ]
                }} styles={{
                    root: {
                        height: "initial !important",
                        minHeight: "initial !important",
                        padding: "0px !important",
                        position: "relative",
                        top: "-1px !important"
                    }
                }} />
            </Stack>
            <Text size={'small'}> on </Text>
            <Stack horizontal>
                <Label size={'xsmall'} className={loaded ? 'color-blue' : 'color-gray'}>{selectedDate.text}</Label>
                <ActionButton disabled={!loaded} menuProps={{
                    items: [
                        {
                            key: 'this month', text: 'this month', onClick: () => {
                                setSelectedDate({
                                    text: 'this month',
                                    date: moment().startOf('month').toISOString(),
                                    period: 'month'
                                });
                            }
                        },
                        {
                            key: 'last month', text: 'last month', onClick: () => {
                                setSelectedDate({
                                    text: 'last month',
                                    date: moment().add(-1, 'month').startOf('month').toISOString(),
                                    period: 'month'
                                });
                            }
                        },
                        {
                            key: 'this year', text: 'this year', onClick: () => {
                                setSelectedDate({
                                    text: 'this year',
                                    date: moment().startOf('year').toISOString(),
                                    period: 'year'
                                });
                            }
                        },
                        {
                            key: 'last year', text: 'last year', onClick: () => {
                                setSelectedDate({
                                    text: 'last year',
                                    date: moment().add(-1, 'year').startOf('year').toISOString(),
                                    period: 'year'
                                });
                            }
                        }
                    ]
                }} styles={{
                    root: {
                        height: "initial !important",
                        minHeight: "initial !important",
                        padding: "0px !important",
                        position: "relative",
                        top: "-1px !important"
                    }
                }} />
            </Stack>
        </Stack>

        {!loaded ? <LoadingComponent label={'Retrieving performance & target recap ...'} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}

        {loaded ? <Stack tokens={{ childrenGap: 20 }}>
            {/* Target Achievement Card */}
            {targetSummary && targetSummary.targetAmount > 0 ? (
                <Stack tokens={{ childrenGap: 12 }} style={{
                    padding: 16,
                    borderRadius: 8,
                    backgroundColor: '#faf9f8',
                    border: '1px solid #edebe9'
                }}>
                    <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'}>
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign={'center'}>
                            <Label size={'medium'}>{selectedDate.period === 'year' ? 'Yearly Target' : 'Monthly Target'}</Label>
                            <Tag size={'small'} text={`Basis: ${targetSummary.targetType === 'profit' ? 'Profit' : 'Omset'}`} />
                        </Stack>
                        <Label size={'medium'} style={{ color: targetSummary.isTargetMet ? '#107c41' : '#0078d4' }}>
                            {targetSummary.percentage}% Achieved
                        </Label>
                    </Stack>

                    <ProgressIndicator
                        percentComplete={Math.min(1, targetSummary.percentage / 100)}
                        barHeight={10}
                    />

                    <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'}>
                        <Stack tokens={{ childrenGap: 2 }}>
                            <Text size={'xsmall'}>Realization ({targetSummary.targetType === 'profit' ? 'Nett Profit' : 'Omset'})</Text>
                            <Label size={'small'}>Rp. {GeneralService.getNumberWithSeparator(targetSummary.achievedAmount)}</Label>
                        </Stack>
                        <Stack tokens={{ childrenGap: 2 }} horizontalAlign={'end'}>
                            <Text size={'xsmall'}>Target Amount {selectedDate.period === 'year' ? '(Setahun)' : '(Sebulan)'}</Text>
                            <Label size={'small'}>Rp. {GeneralService.getNumberWithSeparator(targetSummary.targetAmount)}</Label>
                        </Stack>
                    </Stack>

                    <Stack className={'divider'} />

                    <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'}>
                        <Text size={'xsmall'}>Orders Count: {targetSummary.ordersCount}</Text>
                        {targetSummary.isTargetMet ? (
                            <Label size={'xsmall'} style={{ color: '#107c41', fontWeight: 600 }}>Target Met! 🎉</Label>
                        ) : (
                            <Text size={'xsmall'} style={{ color: '#a19f9d' }}>
                                Remaining: <strong style={{ color: '#323130' }}>Rp. {GeneralService.getNumberWithSeparator(targetSummary.remainingAmount)}</strong>
                            </Text>
                        )}
                    </Stack>
                </Stack>
            ) : (
                <Stack style={{ padding: 12, backgroundColor: '#f3f2f1', borderRadius: 4 }}>
                    <Text size={'small'}>No active target configured for this period.</Text>
                </Stack>
            )}

            {/* General Performance Stats */}
            {performance?.currentPeriod ? (
                <Stack tokens={{ childrenGap: 10 }}>
                    <Stack horizontal verticalAlign={'center'} horizontalAlign={'space-between'}>
                        <Text size={'small'}>Total Revenue (Omset)</Text>
                        <Label>Rp. {GeneralService.getNumberWithSeparator(performance.currentPeriod.totalRevenue)}</Label>
                    </Stack>
                    <Stack horizontal verticalAlign={'center'} horizontalAlign={'space-between'}>
                        <Text size={'small'}>Total Nett Profit</Text>
                        <Label>Rp. {GeneralService.getNumberWithSeparator(performance.currentPeriod.totalProfit)}</Label>
                    </Stack>
                    <Stack horizontal verticalAlign={'center'} horizontalAlign={'space-between'}>
                        <Text size={'small'}>Active Customers / Companies</Text>
                        <Label>{performance.currentPeriod.totalCompanies}</Label>
                    </Stack>
                </Stack>
            ) : null}
        </Stack> : null}
    </Stack>;
};

export default AdministratorDetailsPerformanceRecap;

